// ui/src/lib/billing-api-client.ts
//
// User-facing billing endpoints (docs/plan/stripe-subscriptions.md, Appendix A,
// "Public / user"). Every route 404s when billing is disabled on the install;
// the fetchers below turn that into `null` where a caller can reasonably run
// without billing, and leave it as an ApiError where it cannot.
import { ApiError, apiRequestJson } from "@/lib/api-client";
import { buildAgentApiUrl } from "@/lib/api-config";
import type {
  BillingConfig,
  BillingPlan,
  BillingPlansResponse,
  ChangePlanPreview,
  ChangePlanRequest,
  CheckoutResponse,
  PortalResponse,
  SubscriptionView,
} from "@/types/billing";

const BILLING_DISABLED_CONFIG: BillingConfig = { enabled: false };

/**
 * Anonymous. Tells the login page whether self-signup exists and the rest of
 * the app whether billing is on at all. A 404 (route not mapped) or a network
 * failure both mean "no billing here".
 */
export async function fetchBillingConfig(): Promise<BillingConfig> {
  let response: Response;
  try {
    response = await fetch(buildAgentApiUrl("/api/billing/config"), { cache: "no-store" });
  } catch {
    return BILLING_DISABLED_CONFIG;
  }
  if (response.status === 404) {
    return BILLING_DISABLED_CONFIG;
  }
  if (!response.ok) {
    throw new ApiError(`Request failed (${response.status})`, response.status);
  }
  const payload = (await response.json()) as Partial<BillingConfig> | null;
  return {
    enabled: payload?.enabled === true,
    signupEnabled: payload?.signupEnabled === true,
    publishableKey: payload?.publishableKey ?? null,
    currency: payload?.currency ?? "eur",
  };
}

/** Active plans with their current price. */
export async function fetchBillingPlans(): Promise<BillingPlan[]> {
  const payload = await apiRequestJson<BillingPlansResponse>("/api/billing/plans");
  return payload?.plans ?? [];
}

/**
 * The caller's org subscription. `null` when billing is disabled (404): the
 * hooks map that to the "unlimited" state so limit checks never fire.
 */
export async function fetchOrgSubscription(): Promise<SubscriptionView | null> {
  try {
    return await apiRequestJson<SubscriptionView>("/api/billing/subscription");
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** org:owner. Writes a pending row and returns the hosted Checkout URL. */
export async function createCheckoutSession(planSlug: string): Promise<CheckoutResponse> {
  return apiRequestJson<CheckoutResponse>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ planSlug }),
  });
}

/** org:owner. Customer Portal session (card update, invoices, cancel). */
export async function createPortalSession(): Promise<PortalResponse> {
  return apiRequestJson<PortalResponse>("/api/billing/portal", {
    method: "POST",
  });
}

/** org:owner. What a switch to `planSlug` would overflow, and what can be trimmed. */
export async function fetchChangePlanPreview(planSlug: string): Promise<ChangePlanPreview> {
  const query = new URLSearchParams({ planSlug });
  return apiRequestJson<ChangePlanPreview>(`/api/billing/change-plan/preview?${query.toString()}`);
}

/** org:owner. Applies the plan change (409 downgrade_requires_selection when the selection is short). */
export async function changePlan(payload: ChangePlanRequest): Promise<SubscriptionView> {
  return apiRequestJson<SubscriptionView>("/api/billing/change-plan", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
